/**
 * Story Time rows on Standout Review. These are storyTimes docs, not
 * standout moments. Share writes isShared true; Unshare writes false.
 */
import { CustomerServiceOutlined } from "@ant-design/icons"
import { App, Tag, Typography } from "antd"
import dayjs from "dayjs"
import { sortBy } from "lodash"
import { useCallback, useMemo } from "react"

import Firebase from "#classes/Firebase"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { STANDARD_DATE_TIME_FORMAT } from "#utils/dateHelpers"
import { isStoryTimeShared } from "#utils/storyTimeHelpers"

import { type ReviewItemSelection } from "./ClassImageReviewList"
import { useFamilyDelivery } from "./familyDeliveryContext"
import { ReviewShareSwitch } from "./MomentShareSwitch"
import { ShareChoice } from "./ShareChoice"
import StandoutFeedRow, {
  FEED_PENDING_PILL,
  FEED_ROLE_PILL,
  FEED_SHARED_PILL,
  FEED_STORY_ICON_BG,
  FEED_STORY_PILL,
  FeedShareCheckbox,
  FeedTypeIcon,
} from "./StandoutFeedRow"
import { itemsSharedMessage } from "./standoutSessionGroups"
import { useSingleFlight } from "./useSingleFlight"

const { Text } = Typography

export const useStoryTimeShareWrite = (
  storyTimeId: string,
  studentId: string,
  classSessionId: string | null | undefined
) => {
  const { message } = App.useApp()
  const delivery = useFamilyDelivery()
  const { pending: isSharing, run: runShare } = useSingleFlight()

  const writeShared = useCallback(
    (nextShared: boolean) =>
      runShare(async () => {
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STORY_TIMES,
            storyTimeId,
            { isShared: nextShared }
          )
          if (nextShared) {
            delivery.reset(studentId, classSessionId)
          } else {
            delivery.unshared(studentId, classSessionId, {
              kind: "storyTime",
              id: storyTimeId,
            })
          }
          void message.success(
            itemsSharedMessage(1, nextShared ? "shared" : "unshared")
          )
        } catch {
          void message.error("Could not update Story Time sharing. Try again.")
        }
      }),
    [classSessionId, delivery, message, runShare, storyTimeId, studentId]
  )

  return { isSharing, writeShared }
}

/** Share switch for one Story Time recording. Hidden without update permission. */
export const StoryTimeShareSwitch = ({
  storyTimeId,
  studentId,
  classSessionId,
  shared,
}: {
  storyTimeId: string
  studentId: string
  classSessionId: string | null | undefined
  shared: boolean
}) => {
  const canShare = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )
  const { isSharing, writeShared } = useStoryTimeShareWrite(
    storyTimeId,
    studentId,
    classSessionId
  )
  if (!canShare) return null
  return (
    <ShareChoice
      state={shared ? "shared" : "open"}
      loading={isSharing}
      onShare={() => void writeShared(true)}
      onUnshare={() => void writeShared(false)}
      shareTestId="sr-story-time-share"
      storyTimeId={storyTimeId}
    />
  )
}

export const StoryTimeReviewRow = ({
  storyTime,
  selection,
  variant = "card",
  authorName = "Teacher",
  roleLabel = "Teacher",
  onAudioHeard,
}: {
  storyTime: StoryTimeConstructor
  selection?: ReviewItemSelection
  variant?: "card" | "feed"
  authorName?: string
  roleLabel?: string
  onAudioHeard?: (heardKey: string) => void
}) => {
  const theme = useThemeContext()
  const canShare = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )
  const shared = isStoryTimeShared(storyTime)
  const feed = variant === "feed"
  const { isSharing, writeShared } = useStoryTimeShareWrite(
    storyTime.id,
    storyTime.studentId,
    storyTime.classSessionId
  )

  const checkbox =
    feed && canShare && (shared || selection) ? (
      <FeedShareCheckbox
        testId="sr-story-time-checkbox"
        shared={shared}
        checked={!!selection?.checked}
        disabled={isSharing}
        ariaLabel={shared ? "Unshare Story Time" : "Select Story Time"}
        dataAttrs={{ "data-story-time-id": storyTime.id }}
        onChange={(checked) => {
          if (shared) {
            if (!checked) void writeShared(false)
            return
          }
          selection?.onChange(checked)
        }}
      />
    ) : null

  const shareSwitch =
    canShare && !feed ? (
      <ReviewShareSwitch
        testId="sr-story-time-share"
        storyTimeId={storyTime.id}
        shared={shared}
        loading={isSharing}
        onToggle={() => void writeShared(!shared)}
      />
    ) : null

  const player = (
    <audio
      controls
      preload="none"
      src={storyTime.url}
      data-testid="sr-story-time-audio"
      style={{ width: "100%" }}
      onPlay={() => onAudioHeard?.(`story:${storyTime.id}`)}
    />
  )

  if (variant === "feed") {
    return (
      <StandoutFeedRow
        testId="sr-story-time"
        selection={checkbox}
        shared={shared}
        rowProps={{
          "data-story-time-id": storyTime.id,
          "data-shared": shared ? "true" : "false",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FeedTypeIcon background={FEED_STORY_ICON_BG}>
            <CustomerServiceOutlined />
          </FeedTypeIcon>
          <Text strong>{authorName}</Text>
          <span style={FEED_ROLE_PILL}>{roleLabel}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(storyTime.timeCreated).format("h:mm A")}
          </Text>
          <span style={FEED_STORY_PILL}>Story Time</span>
        </div>
        {player}
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span
            data-testid={
              shared ? "sr-story-time-shared" : "sr-story-time-unshared"
            }
            style={shared ? FEED_SHARED_PILL : FEED_PENDING_PILL}
          >
            {shared ? "Shared" : "Pending"}
          </span>
        </div>
      </StandoutFeedRow>
    )
  }

  return (
    <div
      data-testid="sr-story-time"
      data-story-time-id={storyTime.id}
      data-shared={shared ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${theme.border}`,
        backgroundColor: theme.gray[1],
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Text strong>Story Time</Text>
        <Text type="secondary">{storyTime.filename || "Recording"}</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {dayjs(storyTime.timeCreated).format(STANDARD_DATE_TIME_FORMAT)}
        </Text>
        {shared ? (
          <Tag color="green" data-testid="sr-story-time-shared">
            Shared
          </Tag>
        ) : (
          <Tag data-testid="sr-story-time-unshared">Not shared</Tag>
        )}
        {shareSwitch}
      </div>
      {player}
    </div>
  )
}

const StoryTimeReviewList = ({
  storyTimes,
  selectionFor,
}: {
  storyTimes: readonly StoryTimeConstructor[]
  selectionFor?: (
    storyTime: StoryTimeConstructor
  ) => ReviewItemSelection | undefined
}) => {
  const ordered = useMemo(
    () => sortBy(storyTimes, (storyTime) => -(storyTime.timeCreated ?? 0)),
    [storyTimes]
  )
  if (ordered.length === 0) return null
  return (
    <div
      data-testid="sr-story-times"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      {ordered.map((storyTime) => (
        <StoryTimeReviewRow
          key={storyTime.id}
          storyTime={storyTime}
          selection={selectionFor?.(storyTime)}
        />
      ))}
    </div>
  )
}

export const StoryTimeStudentSections = ({
  storyTimes,
  studentsById,
}: {
  storyTimes: readonly StoryTimeConstructor[]
  studentsById: Record<string, { name?: string } | undefined>
}) => {
  const sections = useMemo(() => {
    const byStudent = new Map<string, StoryTimeConstructor[]>()
    for (const storyTime of storyTimes) {
      const group = byStudent.get(storyTime.studentId)
      if (group) group.push(storyTime)
      else byStudent.set(storyTime.studentId, [storyTime])
    }
    return sortBy([...byStudent.entries()], ([studentId]) =>
      (studentsById[studentId]?.name ?? studentId).toLowerCase()
    )
  }, [storyTimes, studentsById])

  if (sections.length === 0) return null
  return (
    <div
      data-testid="sr-story-time-students"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      {sections.map(([studentId, rows]) => (
        <div
          key={studentId}
          data-testid="sr-story-time-student"
          data-student-id={studentId}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <Text strong>{studentsById[studentId]?.name ?? studentId}</Text>
          <StoryTimeReviewList storyTimes={rows} />
        </div>
      ))}
    </div>
  )
}

export default StoryTimeReviewList
